import { memo, useEffect, useMemo, useState } from 'react';
import { Dropdown, message } from 'antd';
import { useDispatch, useStore } from 'react-redux';
import { updateStream, useList, useRequest } from '@wappsto/blanket';
import { makeStreamSelector, useAppSelector } from '@wappsto/redux';
import { Trans } from 'react-i18next';
import { getRequestErrorMessage } from 'util/helpers';
import qs from 'qs';
import { useTranslation } from 'translations';
import { updateUnreadNotificationCount, useNotificationStatus } from 'hooks';
import NotificationsButton from './components/NotificationsButton';
import NotificationsMenu from './components/NotificationsMenu';
import useClearNotifications from './hooks/useClearNotifications';
import useDeleteNotification from './hooks/useDeleteNotification';
import { SUPPORTED_CODES, TYPE_IDS } from './util/params';
import { DROPDOWN_TRIGGER } from 'util/params';

const NOTIFICATION_COUNT = 5;

const addFilter = (notification) => {
  if (
    SUPPORTED_CODES.includes(notification.base.code) &&
    TYPE_IDS.includes(notification.base.type_ids)
  ) {
    return true;
  }
  return false;
};

const filter = {
  'this_base.code': SUPPORTED_CODES,
};

const query = {
  from_last: true,
  order_by: 'timestamp',
  limit: NOTIFICATION_COUNT,
  ...filter,
};

const STREAM_MAIN = 'stream-main';

const Notifications = memo(() => {
  const { t, tC } = useTranslation('features', 'notifications');
  const store = useStore();
  const dispatch = useDispatch();
  const [visible, setVisible] = useState(false);
  const getStream = useMemo(makeStreamSelector, []);
  const stream = useAppSelector(state => getStream(state, STREAM_MAIN));
  const [messageApi, contextHolder] = message.useMessage();
  const {
    items: notifications,
    canLoadMore: hasMore,
    request,
    loadMore,
    addItem,
    removeItem,
  } = useList({ url: '/notification', query });
  const { send: countSend, request: countRequest } = useRequest('notifications_count_request');
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [clearNotifications, isClearing] = useClearNotifications(
    unreadNotifications,
    setUnreadNotifications,
    countRequest,
  );
  const updateNotificationStatus = useNotificationStatus(setUnreadNotifications);
  const deleteNotification = useDeleteNotification(setUnreadNotifications);

  useEffect(() => {
    if (countRequest?.status === 'success') {
      setUnreadNotifications(countRequest.json?.count);
    }
  }, [countRequest]);

  useEffect(() => {
    countSend({
      method: 'GET',
      url: `/notification?this_read==unread&${qs.stringify(filter, { indices: false })}`,
    });
  }, [countSend]);

  // listen to stream
  // filter notifications we want to show
  // update unread count
  useEffect(() => {
    const listener = (e) => {
      try {
        let data = JSON.parse(e.data);
        if (data.constructor !== Array) {
          data = [data];
        }
        data.forEach((msg) => {
          if (msg?.meta_object?.type !== 'notification') return;

          const notification = msg.notification || msg.data;
          switch (msg.event) {
            case 'create': {
              // check if you want to add the notification first
              if (!addFilter(notification)) break;

              const newItem = addItem(notification.meta.id);
              if (newItem && notification.read === 'unread') {
                setUnreadNotifications((n) => n + 1);
                if (countRequest.json === undefined) {
                  countRequest.json = { id: [] };
                }
                if (countRequest.json.id === undefined) {
                  countRequest.json.id = [];
                }
                countRequest.json.id.push(notification.meta.id);
              }
              // if (notification.base.code === 1100028) {
              //   handleBackgroundNotification(store, notification);
              // }
              break;
            }
            case 'update': {
              if (notification.read === 'unread') break;

              updateUnreadNotificationCount(
                notification.meta.id,
                countRequest,
                setUnreadNotifications,
              );
              // else if (notification.base.code === 1100028) {
              //   handleBackgroundNotification(store, notification);
              // }
              break;
            }
            case 'delete': {
              removeItem(msg.meta_object.id);
              updateUnreadNotificationCount(
                msg.meta_object.id,
                countRequest,
                setUnreadNotifications,
              );
              break;
            }
            default:
              break;
          }
        });
      } catch (error) {
        // can't parse stream message
        console.error(error);
      }
    };
    if (stream?.ws) {
      stream.ws.addEventListener('message', listener);
    }
    return () => {
      if (stream?.ws) {
        stream.ws.removeEventListener('message', listener);
      }
    };
  }, [stream?.ws, countRequest, addItem, removeItem, store]);

  // subscribe to stream
  useEffect(() => {
    updateStream(dispatch, ['/2.1/notification'], 'add');
    return () => {
      updateStream(dispatch, ['/2.1/notification'], 'remove');
    };
  }, [dispatch]);

  useEffect(() => {
    if (request?.status !== 'error') return;

    const { message: errorMessage } = getRequestErrorMessage(request, tC);
    messageApi.open({
      key: 'notificationsLoadMoreError',
      type: 'error',
      content: (
        <>
          <Trans t={t} i18nKey="notificationsLoadMoreError" />
          <div>{errorMessage}</div>
        </>
      ),
    });
  }, [messageApi, request, t, tC]);

  return (
    <>
      <Dropdown
        trigger={DROPDOWN_TRIGGER}
        open={visible}
        onOpenChange={setVisible}
        popupRender={() => (
          <NotificationsMenu
            notifications={notifications}
            request={request}
            isClearing={isClearing}
            hasMore={hasMore}
            loadMore={loadMore}
            setDropdownVisible={setVisible}
            updateNotificationStatus={updateNotificationStatus}
            clearNotifications={clearNotifications}
            deleteNotification={deleteNotification}
          />
        )}>
        <NotificationsButton count={unreadNotifications} />
      </Dropdown>
      {contextHolder}
    </>
  );
});

Notifications.displayName = 'Notifications';

export default Notifications;
